import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getWindow, trackEvent } from '@surfline/web-common';
import VolumeMute from '@mui/icons-material/VolumeMute';
import VolumeUp from '@mui/icons-material/VolumeUp';
import IconButton from '@mui/material/IconButton';
import Play from 'components/CamPlayerScrubberControls/icons/Play';
import useCamAdRunning from 'utils/useGlobalAdRunningState';
import useClientSize from 'hooks/useClientSize';
import type VideoJsPlayer from 'video.js/dist/types/player';
import videojs from 'utils/videojsWrapper';
import { oneSecond } from 'utils/time';
import config from 'config';
import { SpotDetails } from 'types/spot';
import { useUnmount } from 'react-use';
import { getFallbackAd } from './CamAdPlayerUtils';
import styles from './CamAdPlayer.module.scss';

export const MAX_AD_ERRORS_ALLOWED = 5;
const fullVolume = 0.5;
const AD_FALLBACK_TIMEOUT_MS = 5 * oneSecond;
const AD_PLAYBACK_TIMEOUT_MS = 15 * oneSecond;

interface CamAdPlayerProps {
  adSettings?: {
    adVastTagUrl?: string;
  };
  hasAdFreeCamPermissions: boolean;
  setAdRunning: (adRunning: boolean) => void;
  setPlayAd: (playAd: () => void) => void;
  // Lets the parent call adDisplayContainer.initialize() from inside its click handler.
  // Required so user activation reaches IMA before the async VAST fetch consumes the gesture.
  setInitAdContainer?: (initAdContainer: () => void) => void;
  videoAdPluginsLoaded: boolean;
  spot?: SpotDetails;
  segmentScreen?: string;
}

const CamAdPlayer = ({
  adSettings,
  hasAdFreeCamPermissions,
  setAdRunning,
  setPlayAd,
  setInitAdContainer,
  videoAdPluginsLoaded,
  spot,
  segmentScreen,
}: CamAdPlayerProps) => {
  const adErrorCounter = useRef(0);

  const adDisplayContainerRef = useRef<any>();
  const adsManagerRef = useRef<google.ima.AdsManager>();
  const adsLoaderRef = useRef<google.ima.AdsLoader>();
  const adPlayerRef = useRef<HTMLDivElement>(null);
  const fallbackPlayerRef = useRef<VideoJsPlayer | null>(null);
  const playAdRef = useRef<() => void>();
  // Store timeout ID to clear it when needed
  const adTimeoutRef = useRef<number | null>(null);

  const [adProgress, setAdProgress] = useState(0);
  const adProgressRef = useRef(adProgress);

  useEffect(() => {
    adProgressRef.current = adProgress;
  }, [adProgress]);

  const userWantsAdMuted = useRef(true);
  const [adMuted, setAdMuted] = useState(userWantsAdMuted.current);
  const [adPaused, setAdPaused] = useState(true);
  const [adPlaying, setAdPlaying] = useState(false);

  const [, setCamAdRunningGlobal] = useCamAdRunning();

  const google = getWindow()?.google;

  const segmentProps = useMemo(
    () => ({
      ...(spot && {
        spotId: spot._id,
        spotName: spot.name,
        subregionId: spot.subregion?._id,
        subregionName: spot.subregion?.name,
      }),
      uiElement: 'camera',
      location: 'house advert',
      screen: segmentScreen,
    }),
    [spot, segmentScreen],
  );

  const onClickHandler = useCallback(() => {
    trackEvent('Clicked Subscribe CTA', segmentProps);
    setTimeout(() => {
      window.open(`${config.surflineHost}/upgrade`, '_blank', 'noopener,noreferrer');
    }, 300);
  }, [segmentProps]);

  const clearAdTimeout = useCallback(() => {
    if (adTimeoutRef.current !== null) {
      window.clearTimeout(adTimeoutRef.current);
      adTimeoutRef.current = null;
    }
  }, []);

  const onCompleted = useCallback(() => {
    setAdRunning(false);
    setAdPlaying(false);
    setCamAdRunningGlobal(false);
    adsManagerRef.current?.stop();
    adsManagerRef.current?.destroy();
    adsManagerRef.current = undefined;
    fallbackPlayerRef.current?.dispose();
    fallbackPlayerRef.current = null;

    clearAdTimeout();
  }, [setAdRunning, setCamAdRunningGlobal, clearAdTimeout]);

  // Tear down any in-flight ad on unmount — cam-angle switches remount this component
  // and would otherwise leave the old AdsManager running and adRunning stuck on.
  useUnmount(onCompleted);

  const setAdTimeout = useCallback(
    (callback: (e?: any) => void, timeoutMs: number) => {
      clearAdTimeout();

      adTimeoutRef.current = window.setTimeout(callback, timeoutMs);
    },
    [clearAdTimeout],
  );

  const onAdsError = useCallback(
    (e: any) => {
      console.error('onAdsError', e);
      onCompleted();
      playAdRef.current?.();
      adErrorCounter.current += 1;
    },
    [onCompleted],
  );

  const playFallbackAd = useCallback(() => {
    trackEvent('Detected Ad Blocker');
    if (adPlayerRef.current) {
      // the fallback ad should have finished playing after 30 seconds
      setAdTimeout(onCompleted, 30 * oneSecond);
      setAdRunning(true);
      setCamAdRunningGlobal(true);

      try {
        fallbackPlayerRef.current?.dispose();
        fallbackPlayerRef.current = null;

        if (adPlayerRef.current.firstChild) {
          adPlayerRef.current.innerHTML = '';
        }

        const fallbackAd = getFallbackAd();
        const videoElement = window.document.createElement('video');
        videoElement.setAttribute('id', 'fallback-ad-player');
        videoElement.classList.add('video-js');

        adPlayerRef.current.appendChild(videoElement);

        const player = videojs(videoElement, {
          aspectRatio: '16:9',
          autoplay: true,
          controls: false,
          disablePictureInPicture: true,
          loop: false,
          muted: userWantsAdMuted.current,
          playsinline: true,
          poster: fallbackAd.poster,
          preload: 'auto',
          sources: fallbackAd.sources,
        });

        player.on('error', onCompleted);

        player.addClass('vjs-big-play-centered vjs-sl-cam-player');
        fallbackPlayerRef.current = player;
        setAdPlaying(true);

        player.on('play', () => {
          setAdPaused(false);
        });

        player.on('timeupdate', () => {
          const duration = player?.duration();
          if (duration && duration > 0) {
            const currentTime = player?.currentTime();
            if (currentTime !== undefined) {
              setAdProgress((currentTime / duration) * 100);
            }
          }
        });

        player.on('ended', onCompleted);

        player.play()?.catch((e) => {
          console.error('Error playing fallback ad:', e);
          onCompleted();
        });
      } catch (_e) {
        onCompleted();
      }
    }
  }, [adPlayerRef, onCompleted, setAdRunning, setCamAdRunningGlobal, setAdTimeout]);

  const playAd = useCallback(() => {
    try {
      // stop any existing ads
      onCompleted();

      // if the ad request callback (onAdsManagerLoaded) doesn't fire within 5 seconds, play the fallback ad
      setAdTimeout(playFallbackAd, AD_FALLBACK_TIMEOUT_MS);
      setAdRunning(true);
      setCamAdRunningGlobal(true);

      if (
        google &&
        adSettings?.adVastTagUrl &&
        adPlayerRef.current &&
        adsLoaderRef.current &&
        adErrorCounter.current < MAX_AD_ERRORS_ALLOWED
      ) {
        const adsRequest = new google.ima.AdsRequest();
        adsRequest.adTagUrl = adSettings?.adVastTagUrl;
        adsLoaderRef.current.requestAds(adsRequest);
        setAdProgress(0);
        setAdPaused(true);
      } else {
        playFallbackAd();
      }
    } catch (e) {
      onAdsError(e);
    }
  }, [
    adSettings,
    google,
    playFallbackAd,
    onAdsError,
    setAdTimeout,
    onCompleted,
    setAdRunning,
    setCamAdRunningGlobal,
  ]);

  useEffect(() => {
    setPlayAd(() => {
      adErrorCounter.current = 0;
      playAd();
    });
    playAdRef.current = playAd;
  }, [playAd, setPlayAd]);

  // Expose initialize() to the parent. onAdsManagerLoaded also calls it as a safety net.
  useEffect(() => {
    if (!setInitAdContainer) return;
    setInitAdContainer(() => {
      adDisplayContainerRef.current?.initialize();
    });
  }, [setInitAdContainer]);

  const { clientWidth, clientHeight } = useClientSize(adPlayerRef.current, {
    observeWidth: true,
    observeHeight: true,
  });

  useEffect(() => {
    if (adsManagerRef.current && adPlayerRef.current && google) {
      adsManagerRef.current.resize(clientWidth, clientHeight);
    }
  }, [google, clientWidth, clientHeight]);

  const toggleAdMuted = useCallback(() => {
    if (userWantsAdMuted.current) {
      setAdMuted(false);
      userWantsAdMuted.current = false;
      if (adsManagerRef.current) {
        adsManagerRef.current.setVolume(fullVolume);
      } else if (fallbackPlayerRef.current) {
        fallbackPlayerRef.current.volume(fullVolume);
        fallbackPlayerRef.current.muted(false);
      }
    } else {
      setAdMuted(true);
      userWantsAdMuted.current = true;
      if (adsManagerRef.current) {
        adsManagerRef.current.setVolume(0);
      } else if (fallbackPlayerRef.current) {
        fallbackPlayerRef.current.volume(0);
        fallbackPlayerRef.current.muted(true);
      }
    }
  }, []);

  const resumeAd = useCallback(() => {
    if (adsManagerRef.current) {
      adsManagerRef.current.resume();
    } else if (fallbackPlayerRef.current) {
      fallbackPlayerRef.current.play();
    }
  }, []);

  const updateProgress = useCallback((event: any) => {
    setAdPaused(false);
    if (!adsManagerRef.current) return;
    const duration = event.getAdData()?.duration || 0;
    setAdProgress(((duration - adsManagerRef.current.getRemainingTime()) / duration) * 100);

    // the volume change event doesn't always fire and setVolume doesn't seem to work unless the ad is playing
    if (userWantsAdMuted.current) {
      adsManagerRef.current?.setVolume(0);
    } else {
      adsManagerRef.current?.setVolume(fullVolume);
    }
  }, []);

  const onAdsManagerLoaded = useCallback(
    (event: google.ima.AdsManagerLoadedEvent) => {
      try {
        if (!adPlayerRef.current) {
          throw new Error('adPlayerRef.current is null');
        }

        // For some reason, the type definition for getAdsManager is incorrect,
        // you can run it without passing in a video player
        // @ts-ignore
        const adsManager = event.getAdsManager();

        // if the an ad is already playing then don't start the new ad,
        // this can happen if the ads request doesn't load fast enough
        if (fallbackPlayerRef.current || adsManagerRef.current) {
          adsManager.destroy();
          return;
        }

        // If the ad doesn't start playing within 15 seconds, close the ad player. This sometimes happens
        // on iOS where the ad is playing but never completes as it doesn't update the progress
        setAdTimeout(() => {
          if (adProgressRef.current === 0) {
            onCompleted();
          }
        }, AD_PLAYBACK_TIMEOUT_MS);

        adsManagerRef.current = adsManager;

        adsManagerRef.current.addEventListener(google.ima.AdErrorEvent.Type.AD_ERROR, onAdsError);

        adsManagerRef.current.addEventListener(google.ima.AdEvent.Type.USER_CLOSE, onCompleted);
        adsManagerRef.current.addEventListener(
          google.ima.AdEvent.Type.ALL_ADS_COMPLETED,
          onCompleted,
        );
        adsManagerRef.current.addEventListener(google.ima.AdEvent.Type.SKIPPED, onCompleted);
        adsManagerRef.current.addEventListener(
          google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED,
          onCompleted,
        );
        adsManagerRef.current.addEventListener(google.ima.AdEvent.Type.AD_PROGRESS, updateProgress);

        adsManagerRef.current.addEventListener(google.ima.AdEvent.Type.PAUSED, () =>
          setAdPaused(true),
        );
        adsManagerRef.current.addEventListener(google.ima.AdEvent.Type.RESUMED, () =>
          setAdPaused(false),
        );

        adDisplayContainerRef.current.initialize();
        adsManagerRef.current.init(
          adPlayerRef.current.clientWidth,
          adPlayerRef.current.clientHeight,
        );
        adsManagerRef.current.start();
        setAdPlaying(true);
        adErrorCounter.current = 0;
      } catch (e) {
        onAdsError(e);
      }
    },
    [onAdsError, google, updateProgress, onCompleted, setAdPlaying, setAdTimeout],
  );

  useEffect(() => {
    if (
      hasAdFreeCamPermissions ||
      adPlayerRef.current === null ||
      !videoAdPluginsLoaded ||
      !google?.ima
    ) {
      return;
    }

    adDisplayContainerRef.current = new google.ima.AdDisplayContainer(adPlayerRef.current);

    adsLoaderRef.current = new google.ima.AdsLoader(adDisplayContainerRef.current);

    adsLoaderRef.current.addEventListener(
      (window as any).google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
      onAdsManagerLoaded,
      false,
    );
  }, [hasAdFreeCamPermissions, onAdsManagerLoaded, google, videoAdPluginsLoaded]);

  useUnmount(() => {
    onCompleted();
    setPlayAd(() => {});
  });

  return (
    <div className={styles.camAdPlayerWrapper} data-testid="camAdPlayerWrapper">
      {fallbackPlayerRef.current && (
        <button
          type="button"
          className={styles.clickOverlay}
          onClick={onClickHandler}
          data-testid="clickOverlay"
        >
          Click to upgrade
        </button>
      )}
      <div ref={adPlayerRef} className={styles.camAdPlayer} />
      {adPlaying && (
        <>
          <IconButton
            onClick={toggleAdMuted}
            className={styles.muteButton}
            data-testid="muteButton"
          >
            {adMuted ? <VolumeMute /> : <VolumeUp />}
          </IconButton>
          {adPaused && (
            <IconButton onClick={resumeAd} className={styles.playButton} data-testid="playButton">
              <Play />
            </IconButton>
          )}
          <div
            className={styles.adProgress}
            style={{ width: `${adProgress}%` }}
            data-testid="adProgressBar"
          />
        </>
      )}
    </div>
  );
};

export default memo(CamAdPlayer);
